import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Card, Table, Form, Button, InputGroup, Spinner, Modal, Badge } from "react-bootstrap";
import Swal from "sweetalert2";
import { API_BASE } from "../../services/apirest";

export default function BlacklistModule() {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [newEntry, setNewEntry] = useState({
        nombre: "",
        distribuidor_reporta: "",
        razon_no_recomendable: "",
        region: "",
        rfc: "",
        fecha: new Date().toISOString().split("T")[0]
    });

    const roleId = parseInt(localStorage.getItem("rol_id") || 0);
    // Roles con permiso de escritura (Admin 1 y Analista 14)
    const canEdit = roleId === 1 || roleId === 14;

    const headers = useMemo(() => ({
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
    }), []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/admin_db.php?table=black_list&pk=id`, { headers });
            const json = await res.json();
            if (json.status === "success" || json.ok) {
                const list = json.result.black_list || [];
                // Ordenar por fecha desc
                list.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
                setData(list);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredData = useMemo(() => {
        if (!searchTerm) return data;
        const low = searchTerm.toLowerCase();
        return data.filter(item =>
            Object.values(item).some(v => String(v || "").toLowerCase().includes(low))
        );
    }, [data, searchTerm]);

    const handleAdd = async (e) => {
        e.preventDefault();
        try {
            const res = await fetch(`${API_BASE}/admin_db.php?table=black_list&action=bulk_insert`, {
                method: "POST",
                headers,
                body: JSON.stringify([newEntry])
            });
            const json = await res.json();
            const resultObj = json.result?.result || json.result; // Manejar anidamiento según el backend

            if ((json.status === "success" || json.ok) && resultObj?.inserted > 0) {
                Swal.fire("Éxito", "Usuario agregado a lista negra", "success");
                setShowModal(false);
                setNewEntry({
                    nombre: "",
                    distribuidor_reporta: "",
                    razon_no_recomendable: "",
                    region: "",
                    rfc: "",
                    fecha: new Date().toISOString().split("T")[0]
                });
                fetchData();
            } else {
                const errorMsg = resultObj?.errors?.join("\n") || "No se insertó ningún registro";
                Swal.fire("Incompleto", `Error: ${errorMsg}`, "warning");
            }
        } catch (err) {
            Swal.fire("Error", "No se pudo agregar", "error");
        }
    };

    const handleDelete = async (id) => {
        const resConfirm = await Swal.fire({
            title: "¿Quitar de la Blacklist?",
            text: "El usuario ya no aparecerá como bloqueado.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Sí, quitar"
        });

        if (resConfirm.isConfirmed) {
            try {
                const res = await fetch(`${API_BASE}/admin_db.php?table=black_list&pk=id`, {
                    method: "DELETE",
                    headers,
                    body: JSON.stringify({ id })
                });
                const json = await res.json();
                if (json.status === "success" || json.ok) {
                    Swal.fire("Eliminado", "Registro quitado", "success");
                    fetchData();
                }
            } catch (err) {
                Swal.fire("Error", "Error de servidor", "error");
            }
        }
    };

    return (
        <Container className="my-5 pt-5">
            <Card className="shadow-lg border-0 overflow-hidden" style={{ borderRadius: '15px' }}>
                <Card.Header className="bg-dark text-white p-4 d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="mb-0">Módulo Blacklist <Badge bg="danger" className="ms-2">Seguridad</Badge></h3>
                        <small className="text-secondary">Consulta y gestión de usuarios restringidos</small>
                    </div>
                    {canEdit && (
                        <Button variant="danger" className="rounded-pill px-4 shadow-sm" onClick={() => setShowModal(true)}>
                            + Agregar a Blacklist
                        </Button>
                    )}
                </Card.Header>
                <Card.Body className="p-4">
                    <InputGroup className="mb-4">
                        <InputGroup.Text className="bg-transparent border-end-0">
                            <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Buscar en lista negra (nombre, rfc, distribuidor...)"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border-start-0"
                        />
                    </InputGroup>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="grow" variant="danger" />
                            <p className="mt-2 text-muted">Cargando registros...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Nombre Completo</th>
                                        <th>RFC</th>
                                        <th>Distribuidor</th>
                                        <th>Región</th>
                                        <th>Motivo</th>
                                        {canEdit && <th className="text-center">Acciones</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredData.map((item) => (
                                        <tr key={item.id}>
                                            <td className="text-nowrap">{item.fecha}</td>
                                            <td className="fw-bold">{item.nombre}</td>
                                            <td><Badge bg="light" text="dark" className="border">{item.rfc}</Badge></td>
                                            <td>{item.distribuidor_reporta}</td>
                                            <td>{item.region}</td>
                                            <td>{item.razon_no_recomendable}</td>
                                            {canEdit && (
                                                <td className="text-center">
                                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.id)}>
                                                        Quitar
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                    {filteredData.length === 0 && (
                                        <tr>
                                            <td colSpan={canEdit ? 7 : 6} className="text-center p-5 text-muted">
                                                No se encontraron registros en la lista negra.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Modal para agregar */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">Nuevo Registro en Blacklist</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAdd}>
                    <Modal.Body className="p-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Nombre Completo</Form.Label>
                            <Form.Control
                                required
                                value={newEntry.nombre}
                                onChange={(e) => setNewEntry({ ...newEntry, nombre: e.target.value })}
                                placeholder="Ej: JUAN PEREZ..."
                            />
                        </Form.Group>
                        <div className="row">
                            <Form.Group className="mb-3 col-6">
                                <Form.Label className="small fw-bold">RFC</Form.Label>
                                <Form.Control
                                    value={newEntry.rfc}
                                    onChange={(e) => setNewEntry({ ...newEntry, rfc: e.target.value.toUpperCase() })}
                                    placeholder="ABCD000000XXX"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3 col-6">
                                <Form.Label className="small fw-bold">Región</Form.Label>
                                <Form.Control
                                    value={newEntry.region}
                                    onChange={(e) => setNewEntry({ ...newEntry, region: e.target.value })}
                                    placeholder="PACIFICO, SURESTE..."
                                />
                            </Form.Group>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Distribuidor / Empresa</Form.Label>
                            <Form.Control
                                value={newEntry.distribuidor_reporta}
                                onChange={(e) => setNewEntry({ ...newEntry, distribuidor_reporta: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small fw-bold">Motivo del Bloqueo</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={newEntry.razon_no_recomendable}
                                onChange={(e) => setNewEntry({ ...newEntry, razon_no_recomendable: e.target.value })}
                                placeholder="Describa el motivo de seguridad..."
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" onClick={() => setShowModal(false)}>Cancelar</Button>
                        <Button variant="danger" type="submit">Guardar Bloqueo</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}
